import { useState, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';
import { POSTCODE_REGEX } from '@zoopla/shared/forms';
import { Select } from '@zoopla-eng/zds/select';
import { triggerTracking } from '@zoopla/tracking';
import { SearchSmall } from '@zoopla-eng/zds/icons';
import { ButtonLeft } from '@zoopla-eng/zds/button';
import { InputField } from '@zoopla-eng/zds/input-field';

import {
    callToActionRowStyle,
    propertyBlockStyle,
    textFieldWrapperStyle
} from './GetHomeWorthEstimateForm.styles.css';
import { redirect } from './utils/redirect';
import { Address } from '../graphql.generated';
import { getTrackedPropertyTaxonomyData } from '@zoopla/analytics/property-data';
import { GetHomeWorthEstimateFormProps } from './GetHomeWorthEstimateForm.types';
import { QUERY_UPRN_PROPERTIES_IN_POSTCODE } from './queries/addressByPostcode.query';
import { labelStyle } from './GetHomeWorthEstimateForm.styles.css';
import { fontGroups } from '@zoopla-eng/zds/themes';

const defaultLabels = {
    postcodeLabel: 'Enter full postcode, e.g. SW1 0RG',
    addressLabel: 'Address',
    findAddressButtonText: 'Look up postcode',
    getEstimateButtonText: 'Get an instant estimate'
};

const addressDropdownDefaultValue = 'Please select';
const noPostcodeError =
    "We do not recognise that postcode. Check it's for a UK address.";
const propertiesInPostcodeQueryError =
    'There was an error fetching properties for that postcode. Please try again.';
const invalidPostcodeValidationError =
    'Please provide a full, valid postcode e.g. AB1 2CD';

type AddressData = {
    addressesByPostcode: {
        addresses: Address[];
    };
};

export const formatPostcode = (postcode: string) => {
    const trimmedPostcode = postcode.trim();

    if (trimmedPostcode.length <= 3 || trimmedPostcode.indexOf(' ') > -1) {
        return trimmedPostcode;
    }

    const postcodeLength = trimmedPostcode.length;
    // the outcode is always 3 characters long
    const outcode = trimmedPostcode.slice(postcodeLength - 3, postcodeLength);
    const postcodeStart = trimmedPostcode.slice(0, postcodeLength - 3);

    return `${postcodeStart} ${outcode}`;
};

export const GetHomeWorthEstimateForm = ({
    eventCategory,
    idSuffix,
    labelId,
    initialPostcode = '',
    shouldFocusAfterInitialSearch = false,
    overriddenLabels,
    onSearchDone = () => null,
    onPdpNavigation = () => null,
    contrasted
}: GetHomeWorthEstimateFormProps) => {
    const [properties, setProperties] = useState<Address[]>([]);
    const [isInitialSearchDone, setIsInitialSearchDone] = useState(
        !initialPostcode
    );

    const [selectedUPRN, setSelectedUPRN] = useState<string>('');
    const emptyPropertyOption = {
        id: '',
        text: addressDropdownDefaultValue
    };

    const {
        handleSubmit,
        formState: { isSubmitting, errors },
        setError,
        clearErrors,
        setValue,
        getValues,
        control,
        setFocus
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            postcode: initialPostcode || '',
            property: {
                id: '',
                text: addressDropdownDefaultValue
            }
        }
    });

    const labels = { ...defaultLabels, ...overriddenLabels };

    const getPropertiesByPostcode = async (data: AddressData) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'click',
            eventLabel: 'locate_address'
        });

        const propertiesFromApi = data.addressesByPostcode?.addresses;

        if (propertiesFromApi?.length) {
            setProperties(propertiesFromApi as Address[]);
            clearErrors();

            // we want to avoid focus on the dropdown in specific cases
            const shouldAvoidFocus =
                !isInitialSearchDone && !shouldFocusAfterInitialSearch;
            if (!shouldAvoidFocus) {
                setTimeout(() => {
                    setFocus('property');
                }, 200);
            }
        } else {
            setProperties([]);
            setError('postcode', {
                type: 'manual',
                message: noPostcodeError
            });
        }

        setIsInitialSearchDone(true);
        onSearchDone();
    };

    const [getPostCodeWithUPRN, { loading: loadingUPRNProperties }] =
        useLazyQuery(QUERY_UPRN_PROPERTIES_IN_POSTCODE, {
            onError: function () {
                setProperties([]);
                setError('postcode', {
                    type: 'manual',
                    message: propertiesInPostcodeQueryError
                });
                return;
            },
            onCompleted: getPropertiesByPostcode
        });

    const redirectToPropertyDetailsPage = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'clickthrough',
            eventLabel: 'get_estimate',
            taxonomy: getTrackedPropertyTaxonomyData({ uprn: selectedUPRN })
        });

        const propertyDetailsPageRoute = `/property/uprn/${
            getValues('property').id
        }/`;
        redirect(propertyDetailsPageRoute);
    };

    const hasFetchedPropertiesByPostcode = properties && properties.length > 0;
    const hasEnteredValidPostcode =
        getValues('postcode') &&
        getValues('postcode') !== '' &&
        !errors?.['postcode'];
    const hasSelectedAddressFromDropdown = selectedUPRN !== '';

    const buttonText = hasFetchedPropertiesByPostcode
        ? labels.getEstimateButtonText
        : labels.findAddressButtonText;

    const isButtonDisabled = hasFetchedPropertiesByPostcode
        ? !hasSelectedAddressFromDropdown
        : !hasEnteredValidPostcode;

    const handleChangePostcode = () => {
        setProperties([]);
        setSelectedUPRN('');
    };

    const getPostcodeAddress = useCallback(() => {
        getPostCodeWithUPRN({
            variables: { postcode: formatPostcode(getValues('postcode')) }
        });
    }, [getPostCodeWithUPRN, getValues]);

    const onSubmit = () => {
        if (hasFetchedPropertiesByPostcode) {
            onPdpNavigation();
            redirectToPropertyDetailsPage();
            return;
        }

        getPostcodeAddress();
    };

    useEffect(() => {
        if (initialPostcode && initialPostcode !== '') {
            setIsInitialSearchDone(false);
            setValue('postcode', initialPostcode, { shouldValidate: true });
            getPostcodeAddress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPostcode]);

    const options = properties.map((property) => ({
        id: property.uprn,
        text: property.fullAddress
    }));

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid="home-estimate-form"
        >
            <div className={textFieldWrapperStyle} data-name="text-field">
                {labels.postcodeCaption ? (
                    <label
                        htmlFor={idSuffix ? `postcode-${idSuffix}` : 'postcode'}
                        className={`${labelStyle({ contrasted })} ${
                            fontGroups.ladle
                        }`}
                    >
                        {labels.postcodeCaption}
                    </label>
                ) : null}
                <Controller
                    control={control}
                    rules={{
                        required: true,
                        validate: (value) => value !== '',
                        pattern: {
                            value: POSTCODE_REGEX,
                            message: invalidPostcodeValidationError
                        }
                    }}
                    name="postcode"
                    render={({ field: { onChange, value } }) => (
                        <InputField
                            name="postcode"
                            label={labels.postcodeLabel}
                            data-testid={
                                idSuffix ? `postcode-${idSuffix}` : 'postcode'
                            }
                            aria-describedby={labelId}
                            onChange={(v) => {
                                onChange(v);
                                handleChangePostcode();
                            }}
                            value={value}
                            errorText={errors?.['postcode']?.message}
                            helpText={labels.postcodeHint}
                        />
                    )}
                />
            </div>

            {hasFetchedPropertiesByPostcode ? (
                <div
                    className={propertyBlockStyle}
                    data-testid="property-wrapper"
                    data-name="property"
                    data-cs-mask
                >
                    <Controller
                        control={control}
                        rules={{
                            required: true,
                            validate: (value) => value.id !== ''
                        }}
                        name="property"
                        render={({ field: { onChange, value, ref } }) => (
                            <Select
                                data-testid={
                                    idSuffix
                                        ? `property-select-${idSuffix}`
                                        : 'property-select'
                                }
                                label={
                                    labels.addressCaption ?? labels.addressLabel
                                }
                                errorText={errors?.property?.message}
                                options={[emptyPropertyOption, ...options]}
                                selectedOption={value}
                                onSelectedOptionChange={(v) => {
                                    onChange(v);
                                    setSelectedUPRN(v.id);
                                }}
                                aria-describedby={labelId}
                                ref={ref}
                            />
                        )}
                    />
                </div>
            ) : null}
            <div className={callToActionRowStyle} data-name="cta">
                <ButtonLeft
                    category="primary"
                    data-testid={
                        idSuffix
                            ? `estimate-button-${idSuffix}`
                            : 'estimate-button'
                    }
                    disabled={isButtonDisabled}
                    loading={loadingUPRNProperties || isSubmitting}
                    loadingText="loading"
                    Icon={SearchSmall}
                    type="submit"
                    value="Submit"
                    data-id={errors?.postcode?.message}
                >
                    {buttonText}
                </ButtonLeft>
            </div>
        </form>
    );
};

export default GetHomeWorthEstimateForm;
